// src/pages/Reports/SuspenseLedger/SuspenseLedgerReport.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  AutoComplete,
  Input,
  Table,
  message,
  Grid,
} from "antd";
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";

import ReportDom from "../../../components/common/ReportDom";
import {
  currentDateStartTime,
  currentDateEndTime,
  dateFormat,
} from "../../../lib/functions";
import { getSuspenseLedgerApi } from "../../../apiservice/reports";
import { getAccountBySeachApi } from "../../../apiservice/setting";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import "../../../components/common/bt.css";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const SuspenseLedgerReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [filters, setFilters] = useState({
    account: null,
    fromDate: currentDateStartTime(dayjs()),
    toDate: currentDateEndTime(dayjs()),
  });

  const [accounts, setAccounts] = useState([]);
  const [accLoading, setAccLoading] = useState(false);

  const [data, setData] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // 🔹 Fetch accounts on mount (default list)
  useEffect(() => {
    fetchAccounts("");
  }, []);

  const fetchAccounts = async (query) => {
    setAccLoading(true);
    try {
      const res = await getAccountBySeachApi({
        query: query?.trim() || "",
        multiType: ["suspense_accounts"],
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    } finally {
      setAccLoading(false);
    }
  };

  const fetchReport = async () => {
    if (!filters.account) {
      message.warning("Please select an account");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        accId: filters.account?.acc_id,
        dateFrom: filters.fromDate,
        dateTo: filters.toDate,
      };
      const res = await getSuspenseLedgerApi(payload);
      setData(res.data.ledger || []);
      setOpeningBalance(res.data.opening_balance || 0);
      setClosingBalance(res.data.closing_balance || 0);
    } catch (err) {
      console.error(err);
      message.error("Error fetching Suspense Ledger");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Suspense Ledger",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1 },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (d) => dayjs(d).format(dateFormat),
    },
    { title: "Particular", dataIndex: "particular" },
    { title: "VCH No", dataIndex: "vch_no" },
    { title: "VCH Type", dataIndex: "vch_type" },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }}>
              Suspense Ledger
            </Title>
          </div>
        }
        extra={
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            { !screens.xs && "Print" }
          </Button>
        }
      />

      {/* Filters */}
<Card className="shadow-md rounded-xl mb-4">
  <Row gutter={[16, 16]}>
    {/* Account */}
    <Col xs={24} sm={12} md={8}>
      <label>Account</label>
      <AutoComplete
        style={{ width: "100%" }}
        options={accounts.map((a) => ({
          label: `${a.acc_name} - ${a.acc_code || ""}`,
          value: a.acc_name,
          obj: a,
        }))}
        onSearch={fetchAccounts}
        onSelect={(_, option) => handleFilterChange("account", option.obj)}
        placeholder="Search Account"
        loading={accLoading}
        allowClear
      >
        <Input />
      </AutoComplete>
    </Col>

    {/* <RecordDateFilter
    filters={filters}
    handleFilterChange={handleFilterChange}
  /> */}
  <CommonDateRangeFilter
  filters={filters}
  onChange={(fromDate, toDate) => {
	handleFilterChange("fromDate", fromDate);
	handleFilterChange("toDate", toDate);
  }}
/>

    {/* Report Button */}
    <Col xs={24} sm={12} md={4} style={{ display: "flex", alignItems: "center" }}>
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={fetchReport}
        loading={loading}
        block
      >
        Report
      </Button>
    </Col>
  </Row>
</Card>


      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
  dataSource={[
    { rowType: "opening" },

    ...data.map((row, i) => ({
      ...row,
      seq: i + 1,
      rowType: "ledger",
    })),

    { rowType: "totals" },
    { rowType: "closing" },
  ]}
  rowKey={(row, index) => index}
  loading={loading}
   pagination={{
    ...pagination,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50"],
  }}
  onChange={handleTableChange}
  bordered
  size="small"
  scroll={{ x: "max-content" }}
>
  {/* No. */}
  <Table.Column
    title="No."
    render={(_, record) =>
      record.rowType === "ledger" ? record.seq : ""
    }
  />

  {/* Date Column */}
  <Table.Column
    title="Date"
    dataIndex="created_date"
    render={(value, record) => {
      if (record.rowType === "opening")
        return {
          children: "Opening Balance :",
          props: { colSpan: 5, style: { fontWeight: 700, textAlign: "right" } }
        };

      if (record.rowType === "totals")
        return {
          children: "Grand Total :",
          props: { colSpan: 5, style: { fontWeight: 700, textAlign: "right" } }
        };

      if (record.rowType === "closing")
        return {
          children: "Closing Balance :",
          props: { colSpan: 5, style: { fontWeight: 700, textAlign: "right" } }
        };

      return dayjs(value).format(dateFormat);
    }}
  />

  {/* Particular */}
  <Table.Column
    title="Particular"
    dataIndex="particular"
    render={(value, record) =>
      record.rowType === "ledger"
        ? value
        : { children: null, props: { colSpan: 0 } }
    }
  />

  {/* VCH No */}
  <Table.Column
    title="VCH No"
    dataIndex="vch_no"
    render={(value, record) =>
      record.rowType === "ledger"
        ? value
        : { children: null, props: { colSpan: 0 } }
    }
  />

  {/* VCH Type */}
  <Table.Column
    title="VCH Type"
    dataIndex="vch_type"
    render={(value, record) =>
      record.rowType === "ledger"
        ? value
        : { children: null, props: { colSpan: 0 } }
    }
  />

  {/* Debit */}
  <Table.Column
    title="Debit"
    dataIndex="debit_amount"
    align="right"
    render={(value, record) => {
      if (record.rowType === "opening")
        return <strong>{format(openingBalance.toFixed(2))}</strong>;

      if (record.rowType === "totals")
        return (
          <strong>
            {format(
              _.sumBy(data, (r) => Number(r.debit_amount || 0)).toFixed(2)
            )}
          </strong>
        );

      return record.rowType === "ledger"
        ? format(Number(value || 0).toFixed(2))
        : "";
    }}
  />

  {/* Credit */}
  <Table.Column
    title="Credit"
    dataIndex="credit_amount"
    align="right"
    render={(value, record) => {
      if (record.rowType === "opening") return "";

      if (record.rowType === "totals")
        return (
          <strong>
            {format(
              _.sumBy(data, (r) => Number(r.credit_amount || 0)).toFixed(2)
            )}
          </strong>
        );

      if (record.rowType === "closing")
        return (
          <strong>{format(closingBalance.toFixed(2))}</strong>
        );

      return record.rowType === "ledger"
        ? format(Number(value || 0).toFixed(2))
        : "";
    }}
  />

  {/* Balance */}
  <Table.Column
    title="Balance"
    dataIndex="balance"
    align="right"
    render={(value, record) => {
      if (record.rowType === "ledger")
        return format(Number(value || 0).toFixed(2));

      return "";
    }}
  />
</Table>
 
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Suspense Ledger"
          mode="report-page"
          filters={filters}
          renderTable={() => {
            const debitTotal = _.sumBy(data, (r) => Number(r.debit_amount || 0));
            const creditTotal = _.sumBy(data, (r) => Number(r.credit_amount || 0));
            return (
              <table className="ledger-print-table">
                <colgroup>
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "29%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "11%" }} />
                </colgroup>
                <tbody>
                  <tr className="ledger-print-heading">
                    <td>No.</td>
                    <td>Date</td>
                    <td>Particular</td>
                    <td>VCH No</td>
                    <td>VCH Type</td>
                    <td style={{ textAlign: "right" }}>Debit</td>
                    <td style={{ textAlign: "right" }}>Credit</td>
                    <td style={{ textAlign: "right" }}>Balance</td>
                  </tr>

                  <tr className="ledger-opening-row">
                    <td colSpan={7} style={{ textAlign: "right" }}>
                      Opening Balance
                    </td>
                    <td style={{ textAlign: "right" }}>{format(Number(openingBalance || 0).toFixed(2))}</td>
                  </tr>

                  {data.map((row, index) => (
                    <tr key={row.id || index}>
                      <td>{index + 1}</td>
                      <td>{row.created_date ? dayjs(row.created_date).format(dateFormat) : "-"}</td>
                      <td>{row.particular}</td>
                      <td>{row.vch_no}</td>
                      <td>{row.vch_type}</td>
                      <td style={{ textAlign: "right" }}>{format(Number(row.debit_amount || 0).toFixed(2))}</td>
                      <td style={{ textAlign: "right" }}>{format(Number(row.credit_amount || 0).toFixed(2))}</td>
                      <td style={{ textAlign: "right" }}>{format(Number(row.balance || 0).toFixed(2))}</td>
                    </tr>
                  ))}

                  <tr className="ledger-total-row">
                    <td colSpan={5} style={{ textAlign: "right" }}>Grand Total</td>
                    <td style={{ textAlign: "right" }}>{format(debitTotal.toFixed(2))}</td>
                    <td style={{ textAlign: "right" }}>{format(creditTotal.toFixed(2))}</td>
                    <td />
                  </tr>

                  <tr className="ledger-closing-row">
                    <td colSpan={7} style={{ textAlign: "right" }}>
                      Closing Balance
                    </td>
                    <td style={{ textAlign: "right" }}>{format(Number(closingBalance || 0).toFixed(2))}</td>
                  </tr>
                </tbody>
              </table>
            );
          }}
        />
      </div>
    </div>
  );
};

export default SuspenseLedgerReport;
